import React, { useContext, useMemo } from 'react'
import './sso-workspace.scss'
import { FormNewLabelVariant } from '../form-new/form-label'
import { FieldSectionContext, UnsavedFieldsContext } from '../form-new/field-meta'
import { bySection, jumpToField, useUnsavedChanges } from './unsaved-changes'
import { StatusStrip, sectionDots } from './status-strip'
import { SectionNav, scrollToSection } from './section-nav'
import { Pill } from './pill'
import { IconView } from '../icon/icon'
import { Tooltip } from './tooltip'

// FormNewLabel inside the workspace: sentence-case semibold labels (the
// labels are written that way), muted hints, and "required" only while a
// required field is empty.
export const WORKSPACE_LABEL_VARIANT = {
  groupClassName: 'sw-field',
  labelClassName: 'sw-label',
  subtextClassName: 'sw-hint',
  requiredClassName: 'sw-required',
  requiredOnlyWhenEmpty: true,
  // Real label[for] + id (not just aria-labelledby): the workspace is a
  // single FormNew per page, so `name` is safe to use as the control's id.
  associateControl: true
}

export const WorkspaceFields = ({ children }) =>
  <FormNewLabelVariant.Provider value={WORKSPACE_LABEL_VARIANT}>{children}</FormNewLabelVariant.Provider>

// The small "Staff" tag the mockup puts after staff-only labels and titles:
// an eye (only staff see it) and the word. Screen readers hear what it
// means rather than the bare word.
export const StaffTag = () =>
  <Tooltip className="sw-staff" text="Only EducationXR staff see and change this. Customer admins don't see it.">
    <IconView />
    <span aria-hidden="true">Staff</span>
    <span className="visually-hidden">Staff only</span>
  </Tooltip>

export const WorkspaceSection = ({ id, title, lede, staff, mode, children }) => {
  const section = useMemo(() => ({ id, title }), [id, title])
  if (staff && mode === 'customer') return null
  return (
    <section className="sw-section" id={id}>
      <h2>{title}{staff && <StaffTag />}</h2>
      {lede && <p className="sw-lede">{lede}</p>}
      <FieldSectionContext.Provider value={section}>
        <WorkspaceFields>{children}</WorkspaceFields>
      </FieldSectionContext.Provider>
    </section>
  )
}

// The section nav with each section's unsaved changes (only under
// UnsavedChangesProvider, which lives inside the form).
const SectionNavUnsaved = props => {
  const { changes } = useUnsavedChanges()
  return <SectionNav {...props} unsaved={bySection(changes)} onJumpField={jumpToField} />
}

// status: the connection's status pill (connection-use.js: Active, Not in
// use, Inactive); a status with a reason says it as the pill's tooltip --
// the header is sticky, so it stays one line. active: the plain Active /
// Inactive pill, when there is no status.
export const WorkspaceShell = ({ title, subtitle, logo, active, status, headerActions, tiles, sections, mode = 'staff', onGo = scrollToSection, children }) => {
  // A tile is a shortcut into a section further down the page -- it must
  // never offer to jump to a section this mode doesn't render (the same
  // staff-only rule SectionNav applies to its own links).
  const visibleIds = new Set(sections.filter(s => mode !== 'customer' || !s.staff).map(s => s.id))
  const visibleTiles = tiles.filter(t => visibleIds.has(t.section))
  const unsavedContext = useContext(UnsavedFieldsContext)

  return (
    <div className="sw-workspace pb-5">
      <header className="sw-head d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div className="d-flex align-items-center gap-3 min-w-0">
          {logo}
          <div className="min-w-0">
            <h1 className="sw-title">{title}</h1>
            {subtitle && <div className="sw-sub">{subtitle}</div>}
          </div>
          {status
            ? status.reason
              ? <Tooltip text={status.reason}><Pill tone={status.tone}>{status.label}</Pill></Tooltip>
              : <Pill tone={status.tone}>{status.label}</Pill>
            : active !== undefined && <Pill tone={active ? 'ok' : null}>{active ? 'Active' : 'Inactive'}</Pill>}
        </div>
        {headerActions && <div className="d-flex gap-2">{headerActions}</div>}
      </header>
      <StatusStrip tiles={visibleTiles} onGo={onGo} />
      <div className="sw-layout">
        {unsavedContext
          ? <SectionNavUnsaved sections={sections} mode={mode} dots={sectionDots(visibleTiles)} />
          : <SectionNav sections={sections} mode={mode} dots={sectionDots(visibleTiles)} />}
        <main>{children}</main>
      </div>
    </div>
  )
}
